import { useCallback, useEffect, useState } from 'react';
import {
  Check, Plus, Trash2, RefreshCw, AlertTriangle, Eye,
} from '../../../icons';
import {
  listAdminHolidays, probeSources, previewImport, applyImport, addManualHoliday, deleteHoliday,
  type HolidayRow, type SourceProbe, type ImportResult, type RequestedSource,
} from '../../../api/holidays';
import { useAdminAuthStore } from '../../../store/adminAuthStore';
import './Holiday.css';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

/**
 * 공휴일 관리 (design-20260603-holiday-admin-import, 2026-06-11 구현).
 *
 * 연도/출처 선택 → 출처 probe → dry-run 미리보기(신규/갱신/동일 diff) → 등록(멱등 upsert).
 * + 임시공휴일 수동 추가 / 단건 삭제(명칭 정정 잔존행 정리).
 * 등록 데이터는 전역 holidays 테이블 — 8443 캘린더 오버레이에 전 사용자 자동 반영.
 *
 * ★★ 2026-08-24 — 이 표는 **전 테넌트가 함께 쓰는 전역 표**라 쓰기를 운영자(SYSTEM_ADMIN)로 조였다.
 *    회사 관리자에게는 **읽기만** 남는다. 회사가 쉬는 날을 더하거나 빼는 일은 8443
 *    「근태 › 회사 일정」에서 하고(`institution_holidays`), **전역 1층은 손대지 않는다**.
 *    화면이 그 사실과 갈 곳을 함께 말한다 — 버튼만 감추면 "왜 안 되지" 가 남는다.
 */

const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1, THIS_YEAR + 2];

const SOURCE_LABELS: Array<{ key: RequestedSource; label: string }> = [
  { key: 'auto', label: '자동 (권장)' },
  { key: 'bundled', label: '번들 데이터' },
  { key: 'existing', label: '기존 확인' },
];

function errMsg(err: unknown, fallback: string): string {
  const d = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
  return d?.message ?? fallback;
}

export function HolidaySection() {
  // 쓰기 권한은 서버가 판정한다(403). 화면은 **미리** 말해 주고 손댈 수 없게 둔다.
  const canWrite = useAdminAuthStore((s) => s.user?.role) === 'SYSTEM_ADMIN';
  const [year, setYear] = useState(THIS_YEAR);
  const [source, setSource] = useState<RequestedSource>('auto');
  const [sources, setSources] = useState<Record<string, SourceProbe>>({});
  const [current, setCurrent] = useState<HolidayRow[]>([]);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 수동 추가 폼
  const [mDate, setMDate] = useState('');
  const [mName, setMName] = useState('');
  const [mSub, setMSub] = useState(false);

  const refresh = useCallback(async (y: number) => {
    setError(null);
    try {
      const [rows, probes] = await Promise.all([listAdminHolidays(y), probeSources(y)]);
      setCurrent(rows);
      setSources(probes);
    } catch (err) {
      setError(errMsg(err, '공휴일 정보를 불러오지 못했습니다.'));
    }
  }, []);

  useEffect(() => {
    setPreview(null);
    void refresh(year);
  }, [year, refresh]);

  async function handlePreview() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setPreview(await previewImport(year, source));
    } catch (err) {
      setPreview(null);
      setError(errMsg(err, '미리보기에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleApply() {
    setBusy(true);
    setError(null);
    try {
      const r = await applyImport(year, source);
      setNotice(`${r.year}년 등록 완료 — 신규 ${r.inserted} · 갱신 ${r.updated} · 동일 ${r.skipped}`);
      setPreview(null);
      await refresh(year);
    } catch (err) {
      setError(errMsg(err, '등록에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleManualAdd() {
    if (!mDate || !mName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await addManualHoliday({ date: mDate, name: mName.trim(), isSubstitute: mSub });
      setNotice(`'${r.holiday.name}' ${r.action === 'inserted' ? '추가' : '갱신'} 완료`);
      setMDate(''); setMName(''); setMSub(false);
      if (Number(mDate.slice(0, 4)) === year) await refresh(year);
    } catch (err) {
      setError(errMsg(err, '수동 추가에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(h: HolidayRow) {
    if (!window.confirm(`'${h.date} ${h.name}' 을(를) 삭제할까요?\n전 사용자 캘린더에서 사라집니다.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteHoliday(h.id);
      setCurrent((prev) => prev.filter((x) => x.id !== h.id));
      setNotice(`'${h.name}' 삭제 완료`);
    } catch (err) {
      setError(errMsg(err, '삭제에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  const probeBadge = (p?: SourceProbe) => p === undefined
    ? null
    : p.available
      ? <span className="hol-probe hol-probe--ok"><Check size={11} /> 사용 가능</span>
      : <span className="hol-probe hol-probe--no" title={p.reason}><AlertTriangle size={11} /> {p.reason ?? '사용 불가'}</span>;

  return (
    <section className="svc-cat svc-cat--holiday">
      <ConsoleScreenHead
        title="공휴일 관리"
        desc="1년치 공휴일을 등록하면 전 사용자 캘린더에 자동 표시됩니다."
      />

      {!canWrite && (
        <div className="admin-console__alert" role="status">
          전역 공휴일은 <b>서비스 운영자</b>가 관리합니다 — 여기서는 <b>보기만</b> 합니다.
          우리 회사만 쉬는 날(창립기념일 등)을 더하거나, 쉬지 않는 날을 빼는 것은
          사용자 사이트의 <b>근태 › 회사 일정</b>에서 합니다. 전역 목록은 그대로 두고 회사 줄만 쌓입니다.
        </div>
      )}
      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {notice && <div className="hol-notice" role="status"><Check size={14} /> {notice}</div>}

      {/* ── 가져오기 (연도/출처 → 미리보기 → 등록) ── */}
      <div className="hol-card">
        <div className="hol-controls">
          <label className="hol-field">
            <span>연도</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={busy}>
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
          </label>
          <label className="hol-field">
            <span>국가</span>
            <select value="KR" disabled><option value="KR">대한민국 (KR)</option></select>
          </label>
          <div className="hol-field hol-field--sources">
            <span>출처</span>
            <div className="hol-source-radios" role="radiogroup" aria-label="데이터 출처">
              {SOURCE_LABELS.map((s) => (
                <label key={s.key} className={`hol-source${source === s.key ? ' hol-source--on' : ''}`}>
                  <input
                    type="radio"
                    name="hol-source"
                    checked={source === s.key}
                    onChange={() => setSource(s.key)}
                    disabled={busy}
                  />
                  {s.label}
                  {s.key !== 'auto' && probeBadge(sources[s.key])}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="hol-actions">
          <button type="button" className="hol-btn" onClick={() => void handlePreview()} disabled={busy}>
            <Eye size={13} /> 미리보기
          </button>
          <button
            type="button"
            className="hol-btn hol-btn--primary"
            onClick={() => void handleApply()}
            disabled={!canWrite || busy || preview === null || preview.inserted + preview.updated === 0}
            title={!canWrite ? '전역 목록 등록은 서비스 운영자만 할 수 있습니다' : preview === null ? '먼저 미리보기를 실행하세요' : undefined}
          >
            <Check size={13} /> 등록(적용)
          </button>
        </div>

        {preview && (
          <div className="hol-preview">
            <div className="hol-preview-head">
              미리보기 — 출처 <b>{preview.source}</b> · {preview.total}건
              (<span className="hol-tag hol-tag--new">신규 {preview.inserted}</span>
              <span className="hol-tag hol-tag--upd">갱신 {preview.updated}</span>
              <span className="hol-tag hol-tag--same">동일 {preview.skipped}</span>)
            </div>
            <ul className="hol-list">
              {preview.items.map((it) => (
                <li key={`${it.date}-${it.name}`} className="hol-row">
                  <span className="hol-row-date">{it.date}</span>
                  <span className="hol-row-name">{it.name}{it.isSubstitute && <em className="hol-sub">대체</em>}</span>
                  <span className={`hol-tag hol-tag--${it.action === 'inserted' ? 'new' : it.action === 'updated' ? 'upd' : 'same'}`}>
                    {it.action === 'inserted' ? '신규' : it.action === 'updated' ? '갱신' : '동일'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ── 등록된 공휴일 + 수동 추가 ── */}
      <div className="hol-card">
        <div className="hol-current-head">
          <b>{year}년 등록된 공휴일 ({current.length})</b>
          <button type="button" className="hol-btn hol-btn--ghost" onClick={() => void refresh(year)} disabled={busy} aria-label="새로고침">
            <RefreshCw size={13} />
          </button>
        </div>
        {current.length === 0 ? (
          <p className="svc-empty">등록된 공휴일이 없습니다 — 위에서 미리보기 후 등록하세요.</p>
        ) : (
          <ul className="hol-list hol-list--current">
            {current.map((h) => (
              <li key={h.id} className="hol-row">
                <span className="hol-row-date">{h.date}</span>
                <span className="hol-row-name">{h.name}{h.isSubstitute && <em className="hol-sub">대체</em>}</span>
                <button
                  type="button"
                  className="hol-row-del"
                  onClick={() => void handleDelete(h)}
                  disabled={!canWrite || busy}
                  title={!canWrite ? '전역 목록 삭제는 서비스 운영자만 할 수 있습니다' : undefined}
                  aria-label={`${h.name} 삭제`}
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {canWrite && (
        <div className="hol-manual">
          <b className="hol-manual-title">임시공휴일 수동 추가</b>
          <div className="hol-manual-row">
            <input type="date" value={mDate} onChange={(e) => setMDate(e.target.value)} disabled={busy} aria-label="날짜" />
            <input
              type="text"
              value={mName}
              onChange={(e) => setMName(e.target.value)}
              placeholder="명칭 (예: 임시공휴일)"
              maxLength={100}
              disabled={busy}
              aria-label="명칭"
            />
            <label className="hol-manual-sub">
              <input type="checkbox" checked={mSub} onChange={(e) => setMSub(e.target.checked)} disabled={busy} />
              대체공휴일
            </label>
            <button type="button" className="hol-btn hol-btn--primary" onClick={() => void handleManualAdd()} disabled={busy || !mDate || !mName.trim()}>
              <Plus size={13} /> 추가
            </button>
          </div>
          <p className="hol-manual-note">
            여기에 넣은 날은 <b>모든 회사</b>에 보입니다. 우리 회사만 쉬는 날이라면 회사 일정(8443)에 넣으세요.
          </p>
        </div>
        )}
      </div>
    </section>
  );
}
